<script lang="tsx">
  import type { PropType, CSSProperties } from 'vue';

  import { computed, defineComponent, unref, toRef } from 'vue';
  import { BasicMenu } from '@/components/Menu';
  import { SimpleMenu } from '@/components/SimpleMenu';
  import { AppLogo } from '@/components/Application';
  import { MenuSearch } from '@/components/MenuSearch';

  import { MenuModeEnum, MenuSplitTyeEnum } from '@/enums/menuEnum';

  import { useMenuSetting } from '@/hooks/setting/useMenuSetting';
  import { ScrollContainer } from '@/components/Container';

  import { useGo } from '@/hooks/web/usePage';
  import { useSplitMenu } from './useLayoutMenu';
  import { openWindow } from '@/utils';
  import { propTypes } from '@/utils/propTypes';
  import { isUrl, StorageUtils } from 'xmgoing-utils';
  import { useRootSetting } from '@/hooks/setting/useRootSetting';
  import { useAppInject } from '@/hooks/web/useAppInject';
  import { useDesign } from '@/hooks/web/useDesign';
  import {useHeaderSetting} from "@/hooks/setting/useHeaderSetting";
  import eventBus from "@/events/eventBus";

  import FooterTrigger from '../trigger/HeaderTrigger.vue';
  // import { log } from 'console';

  export default defineComponent({
    name: 'LayoutMenu',
    props: {
      theme: propTypes.oneOf(['light', 'dark']),

      splitType: {
        type: Number as PropType<MenuSplitTyeEnum>,
        default: MenuSplitTyeEnum.NONE,
      },

      isHorizontal: propTypes.bool,
      // menu Mode
      menuMode: {
        type: [String] as PropType<Nullable<MenuModeEnum>>,
        default: '',
      },
    },
    setup(props) {
      const go = useGo();

      const route = useRoute()

      const isSimplFlowDeal = computed(() => {
        if(unref(route).name&&typeof unref(route).name==='string'){
          return unref(route).name?.includes('simplFlowDeal')
        }
      })

      const {
        getMenuMode,
        getMenuType,
        getMenuTheme,
        getCollapsed,
        getCollapsedShowTitle,
        getAccordion,
        getIsHorizontal,
        getIsSidebarType,
        getSplit,
      } = useMenuSetting();

      const {
        getShowSystemTitle,
      } = useHeaderSetting();

      const { getShowLogo,getNotShowAppMenuFlag } = useRootSetting();

      const { prefixCls } = useDesign('layout-menu');

      const { menusRef } = useSplitMenu(toRef(props, 'splitType'),getNotShowAppMenuFlag);

      const position=StorageUtils.getSessionItem("loginUserType");

      const { getIsMobile } = useAppInject();

      const getComputedMenuMode = computed(() =>
        unref(getIsMobile) ? MenuModeEnum.INLINE : props.menuMode || unref(getMenuMode),
      );

      const getComputedMenuTheme = computed(() => props.theme || unref(getMenuTheme));

      const getIsShowLogo = computed(() => unref(getShowLogo) && unref(getIsSidebarType));

      const getUseScroll = computed(() => {
        return (
          !unref(getIsHorizontal) &&
          (unref(getIsSidebarType) ||
            props.splitType === MenuSplitTyeEnum.LEFT ||
            props.splitType === MenuSplitTyeEnum.NONE)
        );
      });

      const getLogoClass = computed(() => {
        return [
          `${prefixCls}-logo`,
          unref(getComputedMenuTheme),
          {
            [`${prefixCls}--mobile`]: unref(getIsMobile),
          },
        ];
      });

      const getCommonProps = computed(() => {
        const menus = unref(menusRef);
        return {
          menus,
          beforeClickFn: beforeMenuClickFn,
          items: menus,
          theme: unref(getComputedMenuTheme),
          accordion: unref(getAccordion),
          collapse: unref(getCollapsed),
          collapsedShowTitle: unref(getCollapsedShowTitle),
          onMenuClick: handleMenuClick,
        };
      });
      /**
       * click menu
       * @param menu
       */

      function handleMenuClick(path: string) {
        go(path, unref(isSimplFlowDeal));
      }

      onMounted(() => {
        eventBus.off('menuClick', handleMenuClick);
        eventBus.on('menuClick', handleMenuClick);
      })

      /**
       * before click menu
       * @param menu
       */
      async function beforeMenuClickFn(path: string) {
        if (!isUrl(path)) {
          return true;
        }

        openWindow(path);
        return false;
      }

      function renderHeader() {
        if (!unref(getIsShowLogo) && !unref(getIsMobile)) return null;

        return (
          <AppLogo
            showTitle={unref(getCollapsed)&&unref(getShowSystemTitle)}
            class={unref(getLogoClass)}
            theme={unref(getComputedMenuTheme)}
          />
        );
      }

      function renderMenuSearch () {
        if (position !== 'user') {
          return null
        }

        return <MenuSearch />
      }

      function renderMenu() {
        let { menus, ...menuProps } = unref(getCommonProps);

        if (!menus || !menus.length) return null;

        return !props.isHorizontal ? (
          //纵向菜单
          <SimpleMenu {...menuProps} isSplitMenu={unref(getSplit)} items={menus} />
        ) : (
          //横向菜单
          <BasicMenu
            {...(menuProps as any)}
            isHorizontal={props.isHorizontal}
            type={unref(getMenuType)}
            showLogo={unref(getIsShowLogo)}
            mode={unref(getComputedMenuMode as any)}
            items={menus}
          />
        );
      }
      function renderFooterBar() {
        return (
          <div class="footer-bar">
            <FooterTrigger />
          </div>
        )
      }

      return () => {
        return (
          <div class="left-layout">
            {/* {renderHeader()} */}
            {renderMenuSearch()}
            {unref(getUseScroll) ? (
              <ScrollContainer>{() => renderMenu()}</ScrollContainer>
            ) : (
              renderMenu()
            )}
            {renderFooterBar()}
          </div>
        );
      };
    },
  });
</script>
<style lang="less">
  @prefix-cls: ~'@{namespace}-layout-menu';
  @logo-prefix-cls: ~'@{namespace}-app-logo';

  .@{prefix-cls} {
    &-logo {
      height: @header-height;
      padding: 10px 4px 10px 10px;

      img {
        width: @logo-width;
        height: @logo-width;
      }
    }

    &--mobile {
      .@{logo-prefix-cls} {
        &__title {
          opacity: 100%;
        }
      }
    }
  }
  .left-layout{
    height: 100%;
    display: flex;
    flex-direction: column;

    > .scroll-container {
      flex: 1;
      min-height: 0;
      height: auto;
    }
  }
  .footer-bar{
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: auto;
    padding-top: 14px;
    padding-bottom: 16px;
    .vben-layout-header-trigger{
      height: 18px;
      line-height: 18px;
      font-size: 14px;
      color: #999;
      cursor: pointer;
      &:hover{
        color: #666;
      }
    }
  }
</style>
